import React, { useState, useEffect } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import { Favorite, FavoriteBorder } from '@mui/icons-material';
import favoriteService from '../../services/favoriteService';
import { useAuth } from '../../contexts/AuthContext';

const FavoriteButton = ({ providerId, size = 'medium', showTooltip = true, onToggle }) => {
  const { user } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  useEffect(() => {
    if (user && providerId) {
      checkFavoriteStatus();
    }
  }, [user, providerId]);

  useEffect(() => {
    if (!tooltipOpen) {
      return;
    }
    const closeTooltip = () => setTooltipOpen(false);
    window.addEventListener('scroll', closeTooltip, { capture: true, passive: true });
    window.addEventListener('touchmove', closeTooltip, { passive: true });
    return () => {
      window.removeEventListener('scroll', closeTooltip, { capture: true });
      window.removeEventListener('touchmove', closeTooltip);
    };
  }, [tooltipOpen]);

  const checkFavoriteStatus = async () => {
    try {
      const response = await favoriteService.checkFavoriteStatus(providerId);
      setIsFavorite(response.isFavorite);
    } catch (error) {
      console.error('Kedvenc státusz ellenőrzése hiba:', error);
    }
  };

  const handleToggleFavorite = async () => {
    if (!user) {
      return;
    }
    setTooltipOpen(false);

    try {
      setLoading(true);
      const result = await favoriteService.toggleFavorite(providerId);
      setIsFavorite(result.isFavorite);
      if (onToggle) onToggle(result.isFavorite);
    } catch (error) {
      console.error('Kedvenc toggle hiba:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return null; // Don't show for non-authenticated users
  }

  const button = (
    <IconButton
      onClick={handleToggleFavorite}
      disabled={loading}
      color={isFavorite ? 'error' : 'default'}
      size={size}
    >
      {loading ? (
        <CircularProgress size={size === 'small' ? 16 : 24} />
      ) : isFavorite ? (
        <Favorite />
      ) : (
        <FavoriteBorder />
      )}
    </IconButton>
  );

  if (showTooltip) {
    return (
      <Tooltip
        title={isFavorite ? 'Eltávolítás a kedvencekből' : 'Hozzáadás a kedvencekhez'}
        open={tooltipOpen}
        onOpen={() => setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
      >
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default FavoriteButton;